import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import FeedRoundedIcon from '@mui/icons-material/FeedRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import InvoiceBasicData from './InvoiceBasicData';
import InvoiceBillingItems from './InvoiceBillingItems';
import InvoicePaymentConditions from './InvoicePaymentConditions';
import InvoiceCheckout from './InvoiceCheckout';
import InvoiceResumeCard from './InvoiceResumeCard';
import useReloadAlert from '../../../components/shared/boxes/useReloadAlert';
import { useEffect, useState } from 'react';
import { postInvoices } from '../../api/invoices';
import Decimal from 'decimal.js';
import InvoiceBillingItemsWithoutWorks from './InvoiceBillingItemsWithoutWorks';
import { getResourcePlacesSearch } from '../../api/resourcePlaces';
import { Tooltip } from '@mui/material';

const steps = ['Dados Gerais', 'Itens para cobrança', 'Condições de pagamento', 'Finalização'];

const stepIcons = {
    1: <FeedRoundedIcon />,
    2: <ReceiptRoundedIcon />,
    3: <CreditCardRoundedIcon />,
    4: <CheckCircleRoundedIcon />,
};

function CustomStepIconWithLabel(props) {
    const { icon, active, completed } = props;
    const label = steps[icon - 1];

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            <Box color={active ? 'primary.main' : completed ? 'text.primary' : 'text.disabled'}>
                {stepIcons[icon]}
            </Box>
            <Typography
                variant="h7"
                color={active ? 'primary.main' : completed ? 'text.primary' : 'text.disabled'}
            >
                {label}
            </Typography>
        </Stack>
    );
}

const InvoiceMenu = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [skipped, setSkipped] = useState(new Set());
    const [isStepBasicDataValid, setIsStepBasicDataValid] = useState(false);
    const [isStepBillingItemsValid, setIsStepBillingItemsValid] = useState(false);
    const [isStepPaymentConditionsValid, setIsStepPaymentConditionsValid] = useState(false);
    const [isStepBillingItemsWithoutWorksValid, setIsStepBillingItemsWithoutWorksValid] = useState(false);
    const [blockUnload, setBlockUnload] = useState(true);
    const [partsValue, setPartsValue] = useState(0);
    const [worksValue, setWorksValue] = useState(0);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [installmentsQty, setInstallmentsQty] = useState(1);
    const [billingCardHasError, setBillingCardHasError] = useState(false);
    const [confirmedOptions, setConfirmedOptions] = useState([]);
    const [resourceSelected, setResourceSelected] = useState(null);
    const [accountId, setAccountId] = useState(window.currentAccountId);
    const [installmentsAttributes, setInstallmentsAttributes] = useState([]);
    const [performedDate, setPerformedDate] = useState([]);
    const [workIds, setWorkIds] = useState([])
    const [worksDetails, setWorksDetails] = useState([]);
    const [configApiError, setConfigApiError] = useState(null);
    const [loadingWorks, setLoadingWorks] = useState(false);
    const [isAnyCardLoading, setIsAnyCardLoading] = useState(false);
    const [loadingParts, setLoadingParts] = useState(false);
    const [loadingServices, setLoadingServices] = useState(false);
    const [invoicePartsAttributes, setInvoicePartsAttributes] = useState([]);
    const [invoiceServicesAttributes, setInvoiceServicesAttributes] = useState([]);
    const [postInvoicesLoading, setPostInvoicesLoading] = useState(null);
    const [isIntegrated, setIsIntegrated] = useState(false);
    const [invoiceCreated, setInvoiceCreated] = useState(false);

    useReloadAlert(blockUnload);

    const [data, setData] = useState({
        client: null,
        email: null,
        phone: null,
        documentNumber: null,
        invoiceType: '',
        resource: null,
        competenceDate: null,
        payment: '',
        installments: 'À vista',
        manualValues: [],
        valuePayment: '',
        dueDatePayment: '',
        values: [],
        dueDates: [],
        discount: '',
        discountValue: '',
        interest: '',
        fine: '',
        addInformation: '',
        partsCards: [],
        servicesCards: [],
    });

    const hasClientRequiredData = () => {
        if (!data.client) return false;

        const {
            email,
            contact_email,
            phone,
            contact_mobile_phone,
            document_number,
        } = data.client;

        const validEmail = email || contact_email;
        const validPhone = phone || contact_mobile_phone;
        const validDocument = document_number;

        return Boolean(validEmail && validPhone && validDocument);
    };

    const fetchRequiredData = async (clientId) => {
        if (!clientId) return;

        try {
            const result = await getResourcePlacesSearch({
                accountId,
                viewMode: ['Customer'],
                id: clientId,
            });

            const found = result.results?.[0];

            if (found) {
                setData(prev => ({
                    ...prev,
                    client: {
                        ...prev.client,
                        ...found,
                        label: found.name,
                    },
                }));
            }
        } catch (error) {
            setConfigApiError(error.message);
        }
    };

    const stepContents = [
        <InvoiceBasicData
            data={data}
            setData={setData}
            onValidityChange={setIsStepBasicDataValid}
            hasWorksSelected={confirmedOptions.length > 0 || (data.partsCards?.length > 0 || data.servicesCards?.length > 0)}
            accountId={accountId}
        />,
        data.invoiceType === "has_work" ? (
            <Box sx={{ display: 'flex', gap: '1.5rem', my: '3rem', alignItems: 'flex-start' }}>
                <InvoiceBillingItems
                    data={data}
                    setData={setData}
                    onValidityChange={setIsStepBillingItemsValid}
                    partsValue={partsValue}
                    setPartsValue={setPartsValue}
                    worksValue={worksValue}
                    setWorksValue={setWorksValue}
                    confirmedOptions={confirmedOptions}
                    setConfirmedOptions={setConfirmedOptions}
                    billingCardHasError={billingCardHasError}
                    setBillingCardHasError={setBillingCardHasError}
                    resourceSelected={resourceSelected}
                    setResourceSelected={setResourceSelected}
                    onWorkIdChange={setWorkIds}
                    onWorksDetails={setWorksDetails}
                    loadingWorks={loadingWorks}
                    setLoadingWorks={setLoadingWorks}
                    isAnyCardLoading={isAnyCardLoading}
                    setIsAnyCardLoading={setIsAnyCardLoading}
                    onLoadingChange={setIsAnyCardLoading}
                    sx={{ flexGrow: 2, flexBasis: '66%' }}
                />
                <InvoiceResumeCard
                    partsValue={partsValue}
                    worksValue={worksValue}
                    discountAmount={discountAmount}
                    installmentsQty={installmentsQty}
                    loading={isAnyCardLoading}
                />
            </Box>
        ) : (
            <Box sx={{ display: 'flex', gap: '1.5rem', my: '3rem', alignItems: 'flex-start' }}>
                <InvoiceBillingItemsWithoutWorks
                    data={data}
                    setData={setData}
                    loadingParts={loadingParts}
                    setLoadingParts={setLoadingParts}
                    loadingServices={loadingServices}
                    setLoadingServices={setLoadingServices}
                    onValidityChange={setIsStepBillingItemsWithoutWorksValid}
                    partsValue={partsValue}
                    setPartsValue={setPartsValue}
                    worksValue={worksValue}
                    setWorksValue={setWorksValue}
                    onInvoicePartsChange={setInvoicePartsAttributes}
                    onInvoiceServicesChange={setInvoiceServicesAttributes}
                />
                <InvoiceResumeCard
                    partsValue={partsValue}
                    worksValue={worksValue}
                    discountAmount={discountAmount}
                    installmentsQty={installmentsQty}
                    loading={isAnyCardLoading}
                />
            </Box>
        ),
        <Box sx={{ display: 'flex', gap: '1.5rem', my: '3rem', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
                <InvoicePaymentConditions
                    data={data}
                    setData={setData}
                    onValidityChange={setIsStepPaymentConditionsValid}
                    partsValue={partsValue}
                    setPartsValue={setPartsValue}
                    worksValue={worksValue}
                    setWorksValue={setWorksValue}
                    onDiscountChange={setDiscountAmount}
                    onInstallmentsQtyChange={setInstallmentsQty}
                    onInstallmentsAttributesChange={setInstallmentsAttributes}
                    onPerformedDateChange={setPerformedDate}
                    clientHasRequiredData={hasClientRequiredData()}
                    onAutomatedChange={setIsIntegrated}
                />
            </Box>
            <Box sx={{ alignSelf: 'flex-start' }}>
                <InvoiceResumeCard
                    partsValue={partsValue}
                    worksValue={worksValue}
                    discountAmount={discountAmount}
                    installmentsQty={installmentsQty}
                />
            </Box>
        </Box>,
        <Box sx={{ display: 'flex', gap: '1.5rem', my: '3rem', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
                <InvoiceCheckout
                    data={data}
                    setData={setData}
                    apiError={configApiError}
                />
            </Box>
            <Box sx={{ alignSelf: 'flex-start' }}>
                <InvoiceResumeCard
                    partsValue={partsValue}
                    worksValue={worksValue}
                    discountAmount={discountAmount}
                    installmentsQty={installmentsQty}
                />
            </Box>
        </Box>,
    ];

    const isNextButtonDisabled =
        (activeStep === 0 && !isStepBasicDataValid) ||
        (activeStep === 1 && !(isStepBillingItemsValid || isStepBillingItemsWithoutWorksValid)) ||
        (activeStep === 2 && !isStepPaymentConditionsValid) ||
        isAnyCardLoading;

    const hasNoItemsWithoutWorks =
        data.invoiceType === "without_work" &&
        data.partsCards.length === 0 &&
        data.servicesCards.length === 0;

    const nextButtonTooltip = (loadingWorks || loadingParts || loadingServices)
        ? "Aguardando carregamento das informações"
        : hasNoItemsWithoutWorks
            ? "Adicione um item para continuar"
            : "Preencha os campos obrigatórios para continuar";

    const disableReloadAlert = useReloadAlert(blockUnload);
    const isStepSkipped = (step) => skipped.has(step);

    const handleNext = async () => {
        if (isNextButtonDisabled) {
            return;
        }

        let newSkipped = skipped;

        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }

        if (activeStep === 2 && !hasClientRequiredData() && data.client?.id) {
            await fetchRequiredData(data.client.id);
        }

        setActiveStep((prev) => prev + 1);
        setSkipped(newSkipped);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleFinish = async () => {
        disableReloadAlert();
        setBlockUnload(false);

        const subtotal = new Decimal(partsValue).plus(worksValue);
        const totalValue = subtotal.minus(discountAmount || 0);
        const isAutomaticPayment = data.paymentType?.endsWith('_automatic');
        const cleanPaymentType = isAutomaticPayment
            ? data.paymentType.replace('_automatic', '')
            : data.paymentType;

        setPostInvoicesLoading(true)
        const body = {
            invoice: {
                account_id: Number(accountId),
                customer_id: data.client.id,
                work_ids: workIds,
                invoice_type: data.invoiceType,
                performed_date: performedDate,
                subtotal_value: subtotal.toFixed(2),
                total_value: totalValue.toFixed(2),
                subtotal_services_value: worksValue.toFixed(2),
                subtotal_parts_value: partsValue.toFixed(2),
                payment_type: cleanPaymentType,
                integrated: isIntegrated,
                discount: discountAmount,
                interest: data.interest || 0,
                fine: data.fine || 0,
                notes: data.addInformation,
                installments_attributes: installmentsAttributes,
                invoice_parts_attributes: invoicePartsAttributes,
                invoice_services_attributes: invoiceServicesAttributes,
            }
        };
        try {
            const response = await postInvoices({ body });
            const invoiceId = response.id || response.data?.id;
            setInvoiceCreated(true);
            window.location.href = `/invoices/${invoiceId}`;
        } catch (error) {
            setConfigApiError({ errorMessage: error.message });
        }
        finally {
            setPostInvoicesLoading(false)
        }
    };



    return (
        <Box sx={{ width: '100%' }}>
            <Stepper activeStep={activeStep} sx={{ p: '1.5rem', bgcolor: 'background.header', flexWrap: 'wrap', justifyContent: 'center', }}>
                {steps.map((_, index) => (
                    <Step key={index}>
                        <StepLabel
                            slots={{ stepIcon: CustomStepIconWithLabel }}
                        />
                    </Step>
                ))}
            </Stepper>

            <Box>{stepContents[activeStep]}</Box>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    pt: 2,
                    justifyContent: 'space-between',
                }}
            >
                {activeStep > 0 ? (
                    <Button
                        variant="outlined"
                        startIcon={<ChevronLeftIcon />}
                        onClick={handleBack}
                        sx={{ mr: 1 }}
                    >
                        Anterior
                    </Button>
                ) : (
                    <Box sx={{ minWidth: 120 }} />
                )}
                {isNextButtonDisabled ? (
                    <Tooltip title={nextButtonTooltip} placement="top" arrow>
                        <span>
                            <Button
                                onClick={activeStep === steps.length - 1 ? handleFinish : handleNext}
                                disabled={isNextButtonDisabled}
                                endIcon={
                                    activeStep === steps.length - 1 ? (
                                        <CheckCircleRoundedIcon />
                                    ) : (
                                        <ArrowForwardRoundedIcon />
                                    )
                                }
                            >
                                {activeStep === steps.length - 1 ? 'Criar Fatura' : 'Próximo'}
                            </Button>
                        </span>
                    </Tooltip>
                ) : (
                    <Button
                        onClick={activeStep === steps.length - 1 ? handleFinish : handleNext}
                        disabled={isNextButtonDisabled || postInvoicesLoading || invoiceCreated}
                        loading={postInvoicesLoading}
                        loadingPosition="end"
                        endIcon={
                            !postInvoicesLoading &&
                            (activeStep === steps.length - 1
                                ? <CheckCircleRoundedIcon />
                                : <ArrowForwardRoundedIcon />)
                        }
                        sx={{
                            '& .MuiCircularProgress-root': {
                                marginRight: '0.5rem',
                                marginLeft: 0,
                            },
                        }}
                    >
                        {activeStep === steps.length - 1 ? 'Criar Fatura' : 'Próximo'}
                    </Button>)
                }
            </Box>
        </Box>
    );
}

export default InvoiceMenu;